import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  useReactTable, getCoreRowModel, getFilteredRowModel, getSortedRowModel,
  createColumnHelper, flexRender,
  type SortingState, type ColumnFiltersState, type FilterFn,
} from '@tanstack/react-table';
import { MailX, ChevronUp, ChevronDown, ChevronsUpDown, AlertTriangle, Download, RefreshCw, ChevronDown as ChevronDownIcon, Cloud } from 'lucide-react';
import { FilterChip } from '../../_shared/components';
import { Modal } from '../../_shared/components';
import type { CampaignMember, EnrichmentFlag } from './types';
import type { WaterfallRow } from './waterfallTypes';
import { evalRow, evalCondition } from './waterfallTypes';

// ── Styling maps ─────────────────────────────────────────────────────────────

const TIER_CLASSES: Record<string, string> = {
  'Tier 1': 'bg-blueberry-50 text-blueberry-700 dark:bg-blueberry-950/50 dark:text-blueberry-400',
  'Tier 2': 'bg-matcha-50 text-matcha-700 dark:bg-matcha-950/50 dark:text-matcha-400',
  'Tier 3': 'bg-nightshade-100 text-nightshade-600 dark:bg-nightshade-800 dark:text-nightshade-300',
  'Tier 4': 'bg-nightshade-50 text-nightshade-400 dark:bg-nightshade-900 dark:text-nightshade-500',
};

const STATUS_CLASSES: Record<string, string> = {
  Attended:           'bg-matcha-50 text-matcha-700 dark:bg-matcha-950/50 dark:text-matcha-400',
  'No-show':          'bg-pomegranate-50 text-pomegranate-700 dark:bg-pomegranate-950/50 dark:text-pomegranate-400',
  Registered:         'bg-blueberry-50 text-blueberry-700 dark:bg-blueberry-950/50 dark:text-blueberry-400',
  Sent:               'bg-nightshade-100 text-nightshade-600 dark:bg-nightshade-800 dark:text-nightshade-300',
  Declined:           'bg-pomegranate-50 text-pomegranate-600 dark:bg-pomegranate-950/50 dark:text-pomegranate-400',
  'Pending Approval': 'bg-lemon-50 text-lemon-700 dark:bg-lemon-950/50 dark:text-lemon-400',
  Responded:          'bg-matcha-50 text-matcha-600 dark:bg-matcha-950/50 dark:text-matcha-400',
};

// ── Sub-components ────────────────────────────────────────────────────────────

function OwnerCell({ name, email }: { name: string | null; email: string | null }) {
  if (!name) return <span className="text-nightshade-300 dark:text-nightshade-600">—</span>;
  return (
    <div className="flex items-center gap-1.5" title={email ?? name}>
      <span className="flex-shrink-0 w-5 h-5 rounded-full bg-nightshade-200 dark:bg-nightshade-700 text-nightshade-600 dark:text-nightshade-300 text-xs font-semibold flex items-center justify-center">
        {(name[0] ?? '').toUpperCase()}
      </span>
      <span className="truncate max-w-[90px] text-xs text-nightshade-700 dark:text-nightshade-300">{name.split(' ')[0]}</span>
    </div>
  );
}

function SortIcon({ state }: { state: false | 'asc' | 'desc' }) {
  if (state === 'asc') return <ChevronUp className="w-3 h-3 text-blueberry-500" />;
  if (state === 'desc') return <ChevronDown className="w-3 h-3 text-blueberry-500" />;
  return <ChevronsUpDown className="w-3 h-3 text-nightshade-300 dark:text-nightshade-600" />;
}

// ── Column filter dropdown ────────────────────────────────────────────────────

function ColumnFilter({ values, value, onChange }: {
  values: string[];
  value: string;
  onChange: (v: string) => void;
}) {
  if (values.length === 0) return null;
  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      onClick={e => e.stopPropagation()}
      className="mt-1 w-full text-xs bg-white dark:bg-nightshade-800 border border-nightshade-200 dark:border-nightshade-600 rounded px-1.5 py-0.5 text-nightshade-700 dark:text-nightshade-300 focus:outline-none focus:ring-1 focus:ring-blueberry-400 cursor-pointer"
    >
      <option value="">All</option>
      {values.map(v => <option key={v} value={v}>{v}</option>)}
    </select>
  );
}

// ── CRM link helper ───────────────────────────────────────────────────────────

const SFDC_BASE = 'https://clay.lightning.force.com/lightning/r';

function SfdcLink({ id, type }: { id: string | null | undefined; type: 'Contact' | 'Lead' | 'Account' }) {
  if (!id) return null;
  return (
    <a
      href={`${SFDC_BASE}/${type}/${id}/view`}
      target="_blank"
      rel="noopener noreferrer"
      title={`Open ${type} in Salesforce`}
      onClick={e => e.stopPropagation()}
      className="inline-flex items-center text-blueberry-600 hover:text-blueberry-700 dark:text-blueberry-400 dark:hover:text-blueberry-300 transition-colors"
    >
      <Cloud className="w-3.5 h-3.5" />
    </a>
  );
}

// ── Contact modal ─────────────────────────────────────────────────────────────

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] font-mono font-medium text-nightshade-500 dark:text-nightshade-400 uppercase tracking-wider mb-0.5">{label}</dt>
      <dd className="text-sm text-nightshade-800 dark:text-nightshade-100 break-words">
        {value ?? <span className="text-nightshade-300 dark:text-nightshade-600 italic">—</span>}
      </dd>
    </div>
  );
}

function ContactModal({ member, onClose }: { member: CampaignMember; onClose: () => void }) {
  return (
    <Modal open title={member.contact_name ?? 'Member detail'} onClose={onClose} className="max-w-lg">
      <div className="space-y-6 text-sm">
        <section>
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500">Contact</h3>
            <SfdcLink id={member.contact_id} type="Contact" />
            {!member.contact_id && <SfdcLink id={member.lead_id} type="Lead" />}
          </div>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
            <Field label="Name" value={member.contact_name} />
            <Field label="Email" value={member.contact_email
              ? <a href={`mailto:${member.contact_email}`} className="text-blueberry-600 dark:text-blueberry-400 hover:underline">{member.contact_email}</a>
              : null} />
            <Field label="Title" value={member.contact_title} />
            <Field label="CM Status" value={member.cm_status
              ? <span className={`inline-flex px-1.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_CLASSES[member.cm_status] ?? 'bg-nightshade-100 text-nightshade-600'}`}>{member.cm_status}</span>
              : null} />
            <Field label="Owner" value={member.owner_name} />
            <Field label="Owner Role" value={member.owner_role} />
            <Field label="Fit Score" value={member.fit_score != null ? member.fit_score.toString() : null} />
          </dl>
          {member.notes__c && (
            <div className="mt-3">
              <dt className="text-[11px] font-mono font-medium text-nightshade-500 dark:text-nightshade-400 uppercase tracking-wider mb-0.5">Notes</dt>
              <dd className="text-sm text-nightshade-800 dark:text-nightshade-100 whitespace-pre-wrap">{member.notes__c}</dd>
            </div>
          )}
        </section>
        <section>
          <h3 className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 mb-3">Account</h3>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
            <Field label="Name" value={member.account_name} />
            <Field label="Sales Tier" value={member.sales_tier
              ? <span className={`inline-flex px-1.5 py-0.5 rounded-full text-xs font-semibold ${TIER_CLASSES[member.sales_tier] ?? ''}`}>{member.sales_tier}</span>
              : null} />
            <Field label="GTME Owner" value={member.gtme_owner_email
              ? <a href={`mailto:${member.gtme_owner_email}`} className="text-blueberry-600 dark:text-blueberry-400 hover:underline">{member.gtme_owner_name}</a>
              : member.gtme_owner_name} />
            <Field label="Growth Strategist" value={member.gs_owner_email
              ? <a href={`mailto:${member.gs_owner_email}`} className="text-blueberry-600 dark:text-blueberry-400 hover:underline">{member.gs_owner_name}</a>
              : member.gs_owner_name} />
            <Field label="ClayDR" value={member.claydr_email
              ? <a href={`mailto:${member.claydr_email}`} className="text-blueberry-600 dark:text-blueberry-400 hover:underline">{member.claydr_name}</a>
              : member.claydr_name} />
          </dl>
        </section>
        {(member.suppressed || member.no_longer_at_company || member.suppression_reasons) && (
          <section>
            <h3 className="text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 mb-3">Flags</h3>
            <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
              {member.suppressed != null && <Field label="Suppressed" value={member.suppressed ? 'Yes' : 'No'} />}
              {member.suppression_reasons && <Field label="Suppression Reasons" value={member.suppression_reasons} />}
              {member.no_longer_at_company != null && <Field label="Left Company" value={member.no_longer_at_company ? 'Yes' : 'No'} />}
            </dl>
          </section>
        )}
      </div>
    </Modal>
  );
}

// ── Enrichment gap helper ─────────────────────────────────────────────────────

function getEnrichmentGaps(m: CampaignMember): string[] {
  const gaps: string[] = [];
  if (m.gtme_owner_name && !m.gtme_owner_email) gaps.push('Missing GTME email');
  if (m.gs_owner_name && !m.gs_owner_email) gaps.push('Missing GS email');
  if (m.claydr_name && !m.claydr_email) gaps.push('Missing ClayDR email');
  return gaps;
}

// ── Custom filter fns ─────────────────────────────────────────────────────────

// value is a flag id or 'enrichment_gap' or null
const makeFlagFilterFn = (flags: EnrichmentFlag[]): FilterFn<CampaignMember> =>
  (row, _colId, value) => {
    if (!value) return true;
    const m = row.original;
    if (value === 'enrichment_gap') return getEnrichmentGaps(m).length > 0;
    const flag = flags.find(f => f.id === value);
    if (flag) return evalCondition(m, flag.condition);
    return true;
  };


// ── Column helper ─────────────────────────────────────────────────────────────

const col = createColumnHelper<CampaignMember>();

// ── Main component ────────────────────────────────────────────────────────────

interface MemberTableProps {
  members: CampaignMember[];
  waterfallRows?: WaterfallRow[];
  enrichmentFlags?: EnrichmentFlag[];
  isSyncing?: boolean;
  onHandleExcluded?: {
    onDownloadCsv: () => void;
    onReenrich: () => void;
    reenrichState: 'idle' | 'pending' | 'success' | 'error';
  };
}

type FlagFilter = string | null;

export function MemberTable({ members, waterfallRows, enrichmentFlags = [], isSyncing, onHandleExcluded }: MemberTableProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [globalFilter, setGlobalFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [flagFilter, setFlagFilter] = useState<FlagFilter>(null);
  const [tierFilter, setTierFilter] = useState('');
  const [excludedMenuOpen, setExcludedMenuOpen] = useState(false);
  const excludedMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!excludedMenuOpen) return;
    const handler = (e: MouseEvent) => {
      if (excludedMenuRef.current && !excludedMenuRef.current.contains(e.target as Node)) {
        setExcludedMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [excludedMenuOpen]);
  const [bucketFilter, setBucketFilter] = useState('');
  const [ownerFilter, setOwnerFilter] = useState('');
  const [gsFilter, setGsFilter] = useState('');
  const [claydrFilter, setClaydrFilter] = useState('');
  const [selectedMember, setSelectedMember] = useState<CampaignMember | null>(null);

  const uniqueStatuses = useMemo(() => Array.from(new Set(members.map(m => m.cm_status).filter(Boolean) as string[])).sort(), [members]);
  const uniqueTiers = useMemo(() => Array.from(new Set(members.map(m => m.sales_tier).filter(Boolean) as string[])).sort(), [members]);

  // Waterfall bucket per member (first-match-wins)
  const memberBuckets = useMemo(() => {
    if (!waterfallRows?.length) return {} as Record<string, string>;
    return Object.fromEntries(
      members.map(m => {
        const matched = waterfallRows.find(r => evalRow(m, r));
        return [m.id, matched?.name ?? '—'];
      })
    );
  }, [members, waterfallRows]);

  const uniqueBuckets = useMemo(() =>
    waterfallRows?.map(r => r.name).filter(Boolean) ?? [],
  [waterfallRows]);

  const visibleMembers = useMemo(() => {
    if (!bucketFilter) return members;
    return members.filter(m => (memberBuckets[m.id] ?? '—') === bucketFilter);
  }, [members, memberBuckets, bucketFilter]);
  const uniqueOwners = useMemo(() => Array.from(new Set(members.map(m => m.gtme_owner_first_name ?? m.gtme_owner_name).filter(Boolean) as string[])).sort(), [members]);
  const uniqueGS = useMemo(() => Array.from(new Set(members.map(m => m.gs_owner_first_name ?? m.gs_owner_name).filter(Boolean) as string[])).sort(), [members]);
  const uniqueClaydr = useMemo(() => Array.from(new Set(members.map(m => m.claydr_first_name ?? m.claydr_name).filter(Boolean) as string[])).sort(), [members]);

  const flagFilterFn = useMemo(() => makeFlagFilterFn(enrichmentFlags), [enrichmentFlags]);

  // Per-flag match counts
  const flagCounts = useMemo(() => {
    const counts: Record<string, number> = { enrichment_gap: 0 };
    for (const f of enrichmentFlags) counts[f.id] = 0;
    for (const m of members) {
      for (const f of enrichmentFlags) {
        if (evalCondition(m, f.condition)) counts[f.id]++;
      }
      if (getEnrichmentGaps(m).length > 0) counts.enrichment_gap++;
    }
    return counts;
  }, [members, enrichmentFlags]);

  // Per-member matched flags (for row highlight + cell chips)
  const memberFlags = useMemo(() => {
    const map: Record<string, EnrichmentFlag[]> = {};
    for (const m of members) {
      map[m.id] = enrichmentFlags.filter(f => evalCondition(m, f.condition));
    }
    return map;
  }, [members, enrichmentFlags]);

  const columnFilters = useMemo((): ColumnFiltersState => {
    const filters: ColumnFiltersState = [];
    if (statusFilter) filters.push({ id: 'cm_status', value: statusFilter });
    if (flagFilter) filters.push({ id: '_flags', value: flagFilter });
    if (tierFilter) filters.push({ id: 'sales_tier', value: tierFilter });
    if (ownerFilter) filters.push({ id: 'gtme_owner', value: ownerFilter });
    if (gsFilter) filters.push({ id: 'gs_owner', value: gsFilter });
    if (claydrFilter) filters.push({ id: 'claydr', value: claydrFilter });
    return filters;
  }, [statusFilter, flagFilter, tierFilter, ownerFilter, gsFilter, claydrFilter, bucketFilter]);

  const columns = useMemo(() => [
    col.accessor('cm_status', {
      header: 'Status',
      cell: info => info.getValue()
        ? <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold ${STATUS_CLASSES[info.getValue()!] ?? 'bg-nightshade-100 text-nightshade-600'}`}>{info.getValue()}</span>
        : <span className="text-nightshade-300 dark:text-nightshade-600">—</span>,
      filterFn: 'equals',
    }),
    col.accessor('contact_name', {
      header: 'Name',
      cell: info => {
        const m = info.row.original;
        return (
          <button onClick={() => setSelectedMember(m)} className="text-left group">
            <div className="text-xs font-medium text-nightshade-900 dark:text-nightshade-100 truncate max-w-[170px] group-hover:text-blueberry-600 dark:group-hover:text-blueberry-400 transition-colors">
              {m.contact_name ?? <span className="italic text-nightshade-400">Unknown</span>}
            </div>
            <div className="text-xs text-nightshade-400 truncate max-w-[170px]">
              {m.contact_email ?? <span className="text-pomegranate-400 flex items-center gap-1"><MailX className="w-3 h-3" />no email</span>}
            </div>
          </button>
        );
      },
    }),
    col.display({
      id: 'account_name',
      header: 'Account',
      enableSorting: true,
      cell: info => {
        const m = info.row.original;
        if (!m.account_name) return <span className="text-nightshade-300 dark:text-nightshade-600">—</span>;
        return (
          <div className="flex items-center gap-1.5">
            <span className="text-xs text-nightshade-700 dark:text-nightshade-300 truncate max-w-[130px]" title={m.account_name}>{m.account_name}</span>
            <SfdcLink id={m.contact_account_id} type="Account" />
          </div>
        );
      },
      sortingFn: (a, b) => (a.original.account_name ?? '').localeCompare(b.original.account_name ?? ''),
    }),
    col.accessor('sales_tier', {
      header: 'Tier',
      cell: info => info.getValue()
        ? <span className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-semibold ${TIER_CLASSES[info.getValue()!] ?? 'bg-nightshade-100 text-nightshade-500'}`}>{info.getValue()}</span>
        : <span className="text-nightshade-300 dark:text-nightshade-600">—</span>,
      filterFn: 'equals',
    }),
    col.display({
      id: 'gtme_owner',
      header: 'Owner',
      enableSorting: false,
      cell: info => {
        const m = info.row.original;
        return <OwnerCell name={m.gtme_owner_first_name ?? m.gtme_owner_name ?? null} email={m.gtme_owner_email} />;
      },
      filterFn: ((row, _colId, value) => {
        if (!value) return true;
        const m = row.original;
        return (m.gtme_owner_first_name ?? m.gtme_owner_name ?? '') === value;
      }) as FilterFn<CampaignMember>,
    }),
    col.display({
      id: 'gs_owner',
      header: 'GS',
      enableSorting: false,
      cell: info => {
        const m = info.row.original;
        return <OwnerCell name={m.gs_owner_first_name ?? m.gs_owner_name ?? null} email={m.gs_owner_email} />;
      },
      filterFn: ((row, _colId, value) => {
        if (!value) return true;
        const m = row.original;
        return (m.gs_owner_first_name ?? m.gs_owner_name ?? '') === value;
      }) as FilterFn<CampaignMember>,
    }),
    col.display({
      id: 'claydr',
      header: 'ClayDR',
      enableSorting: false,
      cell: info => {
        const m = info.row.original;
        return <OwnerCell name={m.claydr_first_name ?? m.claydr_name ?? null} email={m.claydr_email} />;
      },
      filterFn: ((row, _colId, value) => {
        if (!value) return true;
        const m = row.original;
        return (m.claydr_first_name ?? m.claydr_name ?? '') === value;
      }) as FilterFn<CampaignMember>,
    }),
    col.display({
      id: '_bucket',
      header: 'Bucket',
      enableSorting: false,
      cell: info => {
        const bucket = memberBuckets[info.row.original.id];
        if (!bucket) return <span className="text-nightshade-300 dark:text-nightshade-600">—</span>;
        return (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium bg-nightshade-100 text-nightshade-600 dark:bg-nightshade-800 dark:text-nightshade-300 whitespace-nowrap max-w-[120px] truncate" title={bucket}>
            {bucket}
          </span>
        );
      },
    }),
    col.display({
      id: '_flags',
      header: 'Flags',
      enableSorting: false,
      cell: info => {
        const m = info.row.original;
        const matched = memberFlags[m.id] ?? [];
        return (
          <div className="flex items-center gap-1 flex-wrap">
            {matched.map(f => (
              <span
                key={f.id}
                title={f.name}
                className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${
                  f.type === 'exclusion'
                    ? 'bg-pomegranate-100 text-pomegranate-700 dark:bg-pomegranate-950/40 dark:text-pomegranate-400'
                    : 'bg-lemon-100 text-lemon-700 dark:bg-lemon-950/40 dark:text-lemon-400'
                }`}
              >
                {f.label}
              </span>
            ))}
            {!m.contact_email && <span title="No email address"><MailX className="w-3.5 h-3.5 text-pomegranate-500" /></span>}
            {getEnrichmentGaps(m).length > 0 && (
              <span title={getEnrichmentGaps(m).join(' · ')}>
                <AlertTriangle className="w-3.5 h-3.5 text-tangerine-500" />
              </span>
            )}
          </div>
        );
      },
      filterFn: flagFilterFn,
    }),
  ], [memberBuckets, memberFlags, flagFilterFn]);

  const table = useReactTable({
    data: visibleMembers,
    columns,
    state: { sorting, globalFilter, columnFilters },
    onSortingChange: setSorting,
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    globalFilterFn: (row, _colId, value) => {
      const q = String(value).toLowerCase();
      const m = row.original;
      return [m.contact_name, m.contact_email, m.account_name, m.contact_title]
        .some(f => (f ?? '').toLowerCase().includes(q));
    },
  });

  const rows = table.getRowModel().rows;
  const toggleFlag = (f: FlagFilter) => {
    const next = flagFilter === f ? null : f;
    setFlagFilter(next);
    setStatusFilter('');
  };

  return (
    <>
      {/* Controls */}
      <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
        <div className="flex items-center gap-1.5 flex-wrap">
          {/* Status chips */}
          <FilterChip label="All" count={members.length} active={statusFilter === '' && flagFilter === null} onClick={() => { setStatusFilter(''); setFlagFilter(null); }} colorScheme="blue" size="sm" />
          {uniqueStatuses.map(s => (
            <FilterChip key={s} label={s} count={members.filter(m => m.cm_status === s).length} active={statusFilter === s && flagFilter === null} onClick={() => { setStatusFilter(s); setFlagFilter(null); }} colorScheme="blue" size="sm" />
          ))}

          <span className="w-px h-4 bg-nightshade-200 dark:bg-nightshade-700 mx-1" />

          {/* Flag chips — data-driven enrichment flags */}
          {enrichmentFlags.map(f => {
            const count = flagCounts[f.id] ?? 0;
            if (count === 0) return null;
            const isExclusion = f.type === 'exclusion';
            const isActive = flagFilter === f.id;
            return (
              <button
                key={f.id}
                onClick={() => toggleFlag(f.id)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-150 ${
                  isActive
                    ? isExclusion
                      ? 'bg-pomegranate-500 text-white shadow-sm'
                      : 'bg-lemon-400 text-lemon-900 shadow-sm'
                    : isExclusion
                      ? 'bg-pomegranate-50 dark:bg-pomegranate-950/40 text-pomegranate-700 dark:text-pomegranate-400 border border-pomegranate-200 dark:border-pomegranate-800 hover:bg-pomegranate-100 dark:hover:bg-pomegranate-900/50'
                      : 'bg-lemon-50 dark:bg-lemon-950/40 text-lemon-700 dark:text-lemon-400 border border-lemon-200 dark:border-lemon-800 hover:bg-lemon-100 dark:hover:bg-lemon-900/50'
                }`}
              >
                {f.label}
                <span className={`inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full text-xs px-1 ${
                  isActive
                    ? isExclusion ? 'bg-pomegranate-700 text-white' : 'bg-lemon-600 text-white'
                    : isExclusion ? 'bg-pomegranate-200 dark:bg-pomegranate-900 text-pomegranate-700 dark:text-pomegranate-400' : 'bg-lemon-200 dark:bg-lemon-900 text-lemon-700 dark:text-lemon-400'
                }`}>{count}</span>
              </button>
            );
          })}
          {(flagCounts.enrichment_gap ?? 0) > 0 && (
            <button onClick={() => toggleFlag('enrichment_gap')} className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-all duration-150 ${flagFilter === 'enrichment_gap' ? 'bg-tangerine-400 text-tangerine-900 shadow-sm' : 'bg-tangerine-50 dark:bg-tangerine-950/40 text-tangerine-700 dark:text-tangerine-400 border border-tangerine-200 dark:border-tangerine-800 hover:bg-tangerine-100 dark:hover:bg-tangerine-900/50'}`}>
              <AlertTriangle className="w-3 h-3" />Enrichment gap
              <span className={`inline-flex h-4 min-w-[1rem] items-center justify-center rounded-full text-xs px-1 ${flagFilter === 'enrichment_gap' ? 'bg-tangerine-600 text-white' : 'bg-tangerine-200 dark:bg-tangerine-900 text-tangerine-700 dark:text-tangerine-400'}`}>{flagCounts.enrichment_gap}</span>
            </button>
          )}
        </div>

        <div className="flex items-center gap-2">
          {onHandleExcluded && (
            <div ref={excludedMenuRef} className="relative">
              <button
                onClick={() => setExcludedMenuOpen(o => !o)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-full bg-pomegranate-50 dark:bg-pomegranate-950/30 text-pomegranate-700 dark:text-pomegranate-400 border border-pomegranate-200 dark:border-pomegranate-800 hover:bg-pomegranate-100 dark:hover:bg-pomegranate-900/50 transition-colors whitespace-nowrap"
              >
                Handle excluded
                <ChevronDownIcon className="w-3 h-3" />
              </button>
              {excludedMenuOpen && (
                <div className="absolute right-0 top-full mt-1 z-20 w-48 rounded-xl border border-nightshade-200 dark:border-nightshade-700 bg-white dark:bg-nightshade-900 shadow-lg overflow-hidden">
                  <button
                    onClick={() => { onHandleExcluded.onDownloadCsv(); setExcludedMenuOpen(false); }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-xs text-nightshade-700 dark:text-nightshade-200 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 transition-colors"
                  >
                    <Download className="w-3.5 h-3.5" />
                    Download CSV
                  </button>
                  <button
                    disabled={onHandleExcluded.reenrichState === 'pending' || onHandleExcluded.reenrichState === 'success'}
                    onClick={() => { onHandleExcluded.onReenrich(); setExcludedMenuOpen(false); }}
                    className="flex items-center gap-2 w-full px-3 py-2 text-xs text-nightshade-700 dark:text-nightshade-200 hover:bg-nightshade-50 dark:hover:bg-nightshade-800 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {onHandleExcluded.reenrichState === 'success'
                      ? <span className="text-matcha-600 text-xs">✓</span>
                      : <RefreshCw className={`w-3.5 h-3.5 ${onHandleExcluded.reenrichState === 'pending' ? 'animate-spin' : ''}`} />
                    }
                    {onHandleExcluded.reenrichState === 'success' ? 'Sent to Clay' : 'Send to Clay'}
                  </button>
                </div>
              )}
            </div>
          )}
          <input
            type="text"
            placeholder="Search name, email, account…"
            value={globalFilter}
            onChange={e => setGlobalFilter(e.target.value)}
            className="w-52 px-3 py-1.5 text-xs bg-white dark:bg-nightshade-900 border border-nightshade-200 dark:border-nightshade-700 rounded-lg text-nightshade-900 dark:text-nightshade-50 placeholder:text-nightshade-400 focus:outline-none focus:ring-2 focus:ring-blueberry-400 focus:border-transparent transition"
          />
        </div>
      </div>

      {/* Table */}
      <div className="relative bg-white dark:bg-nightshade-900 ring-1 ring-nightshade-200 dark:ring-nightshade-700 rounded-2xl overflow-hidden">
        {isSyncing && (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/70 dark:bg-nightshade-900/70 backdrop-blur-sm rounded-2xl">
            <div className="flex items-center gap-2 text-xs text-nightshade-500 dark:text-nightshade-400">
              <svg className="w-4 h-4 animate-spin text-blueberry-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
              Syncing members…
            </div>
          </div>
        )}
        <div className="overflow-x-auto overflow-y-auto max-h-72">
          <table className="w-full text-sm border-collapse">
            <thead className="sticky top-0 z-10">
              {table.getHeaderGroups().map(hg => (
                <tr key={hg.id} className="border-b border-nightshade-100 dark:border-nightshade-800 bg-nightshade-100 dark:bg-nightshade-800 backdrop-blur-sm">
                  {hg.headers.map(header => {
                    const canSort = header.column.getCanSort();
                    const colId = header.column.id;
                    const filterValue = colId === 'sales_tier' ? tierFilter
                      : colId === 'gtme_owner' ? ownerFilter
                      : colId === 'gs_owner' ? gsFilter
                      : colId === 'claydr' ? claydrFilter
                      : colId === '_bucket' ? bucketFilter
                      : '';
                    const filterOptions = colId === 'sales_tier' ? uniqueTiers
                      : colId === 'gtme_owner' ? uniqueOwners
                      : colId === 'gs_owner' ? uniqueGS
                      : colId === 'claydr' ? uniqueClaydr
                      : colId === '_bucket' ? uniqueBuckets
                      : [];
                    const setFilter = colId === 'sales_tier' ? setTierFilter
                      : colId === 'gtme_owner' ? setOwnerFilter
                      : colId === 'gs_owner' ? setGsFilter
                      : colId === 'claydr' ? setClaydrFilter
                      : colId === '_bucket' ? setBucketFilter
                      : null;
                    const minW = colId === 'contact_name' ? 'min-w-[180px]' : colId === '_bucket' ? 'min-w-[120px]' : 'min-w-[100px]';
                    return (
                      <th key={header.id} className={`text-left px-3 py-2 text-[11px] font-mono uppercase tracking-wider font-medium text-nightshade-500 whitespace-nowrap ${minW}`}>
                        <div
                          className={`flex items-center gap-1 ${canSort ? 'cursor-pointer select-none hover:text-nightshade-700 dark:hover:text-nightshade-300 transition-colors' : ''}`}
                          onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && <SortIcon state={header.column.getIsSorted()} />}
                        </div>
                        {setFilter && (
                          <ColumnFilter values={filterOptions} value={filterValue} onChange={setFilter} />
                        )}
                      </th>
                    );
                  })}
                </tr>
              ))}
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="text-center py-10 text-xs text-nightshade-400">
                    No members match your filters.
                  </td>
                </tr>
              ) : (
                rows.map((row, i) => {
                  const m = row.original;
                  const isLast = i === rows.length - 1;
                  const matched = memberFlags[m.id] ?? [];
                  const hasExclusion = matched.some(f => f.type === 'exclusion');
                  const hasWarning = matched.some(f => f.type === 'warning');
                  const rowHighlight = hasExclusion
                    ? 'bg-pomegranate-100 dark:bg-pomegranate-950/30'
                    : hasWarning
                      ? 'bg-lemon-100 dark:bg-lemon-950/30'
                      : 'hover:bg-nightshade-50 dark:hover:bg-nightshade-800/40';
                  return (
                    <tr
                      key={row.id}
                      className={`${!isLast ? 'border-b border-nightshade-100 dark:border-nightshade-800' : ''} ${rowHighlight} transition-colors`}
                    >
                      {row.getVisibleCells().map(cell => (
                        <td key={cell.id} className="px-3 py-1.5">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {rows.length > 0 && (
          <div className="px-4 py-2 border-t border-nightshade-100 dark:border-nightshade-800 bg-nightshade-50 dark:bg-nightshade-800/30">
            <span className="text-xs text-nightshade-400">
              {rows.length.toLocaleString()} of {members.length.toLocaleString()} members
            </span>
          </div>
        )}
      </div>

      {selectedMember && (
        <ContactModal member={selectedMember} onClose={() => setSelectedMember(null)} />
      )}
    </>
  );
}
